<template>
  <v-container fluid class="centered-tabs mt-3 mx-1" v-show="showComponent">
    <v-form ref="form">
      <v-row>
        <v-col cols="12">
          <h2 class="primary--text font-weight-bold pt-5 pl-2">
            {{ edit ? $t('book/admin:parameter.edit.titles.edit') : $t('book/admin:parameter.edit.titles.add') }}
          </h2>

          <v-card
              elevation="0"
              flat
              tile
          >
            <v-card-text>
              <v-row justify="space-around">
                <v-col cols="12" md="5">
                  <!-- General Section -->
                  <h4 class="primary--text font-weight-medium">{{ $t('common:general') }}</h4>
                  <!-- Name -->
                  <v-text-field
                      outlined
                      v-model="data.name"
                      :label="$t('common:name')"
                      :rules="[ruleRequired($t('utils:rules.required'))]"
                      clearable
                      tabindex="1"
                  />

                  <!-- Category -->
                  <v-select
                      :items="restMetaInfo"
                      item-text="translatedName"
                      item-value="id"
                      :menu-props="{ bottom: true, offsetY: true }"
                      v-model="data.type"
                      outlined
                      :label="$t('book/admin:parameter.edit.labels.category')"
                      clearable
                      tabindex="2">
                  </v-select>

                  <!-- Sub-Category -->
                  <v-text-field
                      outlined
                      v-model="data.subType"
                      :label="$t('book/admin:parameter.common.sub-type')"
                      tabindex="3"
                  />
                </v-col>
                <v-col cols="12" md="5">
                  <!-- Additional Section -->
                  <h4 class="primary--text font-weight-medium">{{ $t('book/admin:parameter.edit.titles.specific') }}</h4>
                  <!-- Use value -->
                  <v-checkbox
                      color="secondary"
                      v-model="data.useValue"
                      :label="$t('book/admin:parameter.edit.labels.use-value')"
                      @change="updateType"
                      tabindex="4"
                  />

                  <!-- Value type -->
                  <v-select
                      :disabled="!data.useValue"
                      :items="restOptionType"
                      :menu-props="{ bottom: true, offsetY: true }"
                      v-model="data.valueType"
                      outlined
                      :label="$t('book/admin:parameter.common.value-type')"
                      clearable
                      tabindex="5"
                  />
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12">
          <v-card
              elevation="0"
              flat
              tile
              class="pl-3 pt-3 pb-md-3"
          >
            <BaseButton
                color="primary"
                :btn-text="$t('buttons:save')"
                @clickedButton="saveParameter()"
            />

            <TwoButtonDialog
                v-if="edit"
                ref="deleteButton"
                :btn-text="$t('buttons:delete')"
                color="error"
                :dialog-title="$t('dialog:delete-btn-title', {name : $t('book/admin:parameter.edit.dialog-part-1')})"
                :dialog-text="$t('dialog:delete-btn-dialog', {name : $t('book/admin:parameter.edit.dialog-part-2')})"
                :button1-text="$t('buttons:cancel')"
                :button2-text="$t('buttons:delete')"
                @clickedButton1="$refs.deleteButton.showDialog = false"
                @clickedButton2="deleteParameter()"
            />

            <DirtyConfirmButtonDialog
                ref="dirtyDialog"
                :btn-text="$t('buttons:back')"
                color="grey"
                :dialog-title="$t('dialog:dirty-save-btn-title')"
                :dialog-text="$t('dialog:dirty-save-btn-dialog')"
                :button1-text="$t('dialog:dirty-save-btn-1-text')"
                :button2-text="$t('dialog:dirty-save-btn-2-text')"
                @clickedButton="$router.go(-1)"
            />

          </v-card>
        </v-col>
      </v-row>
    </v-form>
  </v-container>
</template>

<script>
import TwoButtonDialog from "@/modules/core/components/TwoButtonDialog/TwoButtonDialog";
import DirtyConfirmButtonDialog from "../../../core/components/TwoButtonDialog/DirtyConfirmButtonDialog";
import Parameter from "../../data/parameter";
import ParameterService from "../../services/parameter-service";
import {ruleRequired} from "../../../core/services/rules";
import MetaInfoService from "../../services/meta-info-service";
import InfoService from "../../../core/services/info-service";
import Utils from "../../../core/services/utils";
import DataUtils from "@/modules/core/services/data-utils";
import MetaInfo from "@/modules/book/data/meta-info";
import BaseButton from "@/modules/core/components/Base/BaseButton";

export default {
  name: "ParameterForm",
  components: {
    TwoButtonDialog,
    DirtyConfirmButtonDialog,
    BaseButton
  },
  data() {
    return {
      data: new Parameter(),
      edit: false,

      restMetaInfo: [],
      restOptionType: [],
      isFetchingParameter: true
    }
  },
  methods: {
    ruleRequired,
    updateType() {
      if (!this.data.useValue) {
        this.data.valueType = null;
      } else {
        this.data.valueType = this.restOptionType[0];
      }
    },
    saveParameter() {
      if (!this.$refs.form.validate()) {
        // TODO scroll to specific form field which doesnt require the rules
        window.scrollTo(0, 0);
        return;
      }

      if (!this.edit) {
        ParameterService.postToAPI(this.data,
          () => {
            Utils.showSuccessSnackbar(this.$t('snackbar:add-success', {name: this.$t('book/admin:parameter.common.parameter')}));
            this.$router.go(-1)
          },
          () => {
            Utils.showFailureSnackbar(this.$t('snackbar:add-failure', {name: this.$t('book/admin:parameter.common.parameter')}))
          }
        )
      } else {
        ParameterService.putToAPI(this.data,
          () => {
            Utils.showSuccessSnackbar(this.$t('snackbar:edit-success', {name: this.$t('book/admin:parameter.common.parameter')}));
            this.$router.go(-1)
          },
          () => {
            Utils.showFailureSnackbar(this.$t('snackbar:edit-failure', {name: this.$t('book/admin:parameter.common.parameter')}))
          }
        )
      }
    },

    deleteParameter() {
      this.$refs.deleteButton.setButtonSettings(true, true, false, true);
      ParameterService.deleteFromAPI(this.data,
        () => {
          Utils.showSuccessSnackbar(this.$t('snackbar:delete-success', {name: this.$t('book/admin:parameter.common.parameter')}));
          this.$router.go(-1)
        },
        () => {
          this.$refs.deleteButton.setButtonSettings(false, false, false, false);
          this.$refs.deleteButton.showDialog = false;
          Utils.showFailureSnackbar(this.$t('snackbar:delete-failure', {name: this.$t('book/admin:parameter.common.parameter')}))
        }
      )
    },

    translateMeta() {
      for(var i = 0; i < this.restMetaInfo.length; i++) {
        var translated = this.restMetaInfo[i].name;
        translated = translated.toLowerCase();
        if(translated.indexOf(' ') >= 0) {
          translated = translated.split(' ').join('-');
        }
        this.restMetaInfo[i].translatedName = this.$t('book/admin:parameter.edit.constants.' + translated);
      }
    }
  },
  computed: {
    currentLang() {
      return this.$store.state.translation.language;
    },
    showComponent() {
      if(this.edit) {
        if(this.isFetchingParameter) {
          return false
        } else {
          return true;
        }
      } else {
        return true
      }
    }
  },
  watch: {
    currentLang() {
      Utils.sleep(500).then(() => {
        this.translateMeta();
        this.$refs.form.resetValidation()
      });
    }
  },
  created() {
    if (this.$route.params.id) {
      this.edit = true

      ParameterService.readFromAPI(this.$route.params.id,
        (resp) => {
          var response = DataUtils.parseDataToSingleObject(resp.data, Parameter);
          if (response == null) {
            this.$router.go(-1);
          } else {
            this.data = response;
            this.isFetchingParameter = false
            this.$watch('data', () => {this.$refs.dirtyDialog.setDirty(true)}, {deep: true});
          }
        },
        (error) => {
          this.$logger.error("Error at ParameterService.readFromAPI: " + error)
          this.$router.push('/404')
        }
      )
    }
    else {
      this.$watch('data', () => {this.$refs.dirtyDialog.setDirty(true)}, {deep: true});
    }

    MetaInfoService.readAllFromAPI(
      (resp) => {
        this.restMetaInfo = DataUtils.parseDataToObjectArray(resp.data, MetaInfo);
        this.translateMeta();
      },
      (error) => {
        Utils.showFailureSnackbar(this.$t('snackbar:load-failure', {name: this.$t('book/admin:parameter.edit.snackbar.meta-info')}))
        this.$logger.error("Error at MetaInfoService.readAllFromAPI: " + error)
      }
    )

    InfoService.readEnum("option/type",
      (resp) => {
        this.restOptionType = resp.data;
      },
      (error) => {
        Utils.showFailureSnackbar(this.$t('snackbar:load-failure', {name: this.$t('book/admin:parameter.edit.snackbar.option-types')}))
        this.$logger.error("Error at InfoService.readEnum: " + error)
      }
    )
  }
}

</script>
<style src="@/styles/app.scss" lang="scss"/>
